import { css, keyframes, type CSSObject } from '@emotion/react'
import {
  CornerRadius,
  cssVar,
  FocusIndicator,
  GemColors,
  LineWeight,
  Shadow,
  ShadowElevation,
  Spacing,
  ThemedColors,
  Timing,
  Transition,
  Typeface,
  Typography,
  wideGamutColor,
  ZIndex,
  type Theme,
} from '@opallabs/gem'

const rootClass = 'lapidary-modal'
export const classes = {
  body: `${rootClass}-body`,
  close: `${rootClass}-close`,
  content: `${rootClass}-content`,
  description: `${rootClass}-description`,
  footer: `${rootClass}-footer`,
  multiStepCarousel: `${rootClass}-multistep-carousel`,
  multiStepCarouselWrapper: `${rootClass}-multistep-carousel-wrapper`,
  multiStep: `${rootClass}-multistep-container`,
  multiStepFooter: `${rootClass}-multistep-footer`,
  multiStepStepTitle: `${rootClass}-multistep-step-title`,
  multiStepPreviousStepButton: `${rootClass}-multistep-button-previous`,
  overlay: `${rootClass}-overlay`,
  progressBar: `${rootClass}-progressbar`,
  title: `${rootClass}-title`,
  titleIcon: `${rootClass}-title-icon`,
  titleText: `${rootClass}-title-text`,
  tabsList: `${rootClass}-tabs-list`,
  tabsTrigger: `${rootClass}-tabs-trigger`,
  tabsContent: `${rootClass}-tabs-content`,
} as const

export const cssVariables = {
  modalPadding: `--${rootClass}-padding`,
} as const

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
})

const fadeOut = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 },
})

export const Overlay = (theme: Theme): CSSObject => ({
  position: 'fixed',
  inset: 0,
  zIndex: ZIndex.Shroud.Overlay,
  backgroundColor: wideGamutColor(
    GemColors.N900,
    theme === 'light' ? 0.15 : 0.5
  ),

  '&[data-state="open"]': {
    animation: `${fadeIn} ${Timing.Fastest * 6}ms cubic-bezier(0.22, 1, 0.36, 1)`,
  },

  '&[data-state="closed"]': {
    animation: `${fadeOut} ${Timing.Fastest * 6}ms cubic-bezier(0.22, 1, 0.36, 1)`,
  },

  '&[data-scrollable="true"]': {
    display: 'grid',
    placeItems: 'center',
    overflow: 'auto',

    '> :is([role=dialog])': {
      margin: Spacing.L,
      position: 'relative',
    },
  },

  '+ :is([role=dialog])': {
    position: 'fixed',
    insetInlineEnd: '50%',
    transform: 'translate(50%, -50%)',
    insetBlockStart: '50%',
  },
})

export const modalWrapper = css({
  backgroundColor: cssVar(ThemedColors.Surface1),
  borderRadius: CornerRadius.XLarge,
  display: 'flex',
  inlineSize: Spacing.XS * 100,
  flexDirection: 'column',
  fontFamily: Typeface.Default,
  zIndex: ZIndex.Shroud.Content,
  ...Shadow[ShadowElevation.E3],
  [cssVariables.modalPadding]: `${Spacing.M * 2}px`,

  // 'border-box' is set globally in Asgard, but not in legacy views.
  // We need to set it here for all descendant elements so the layout
  // works correctly in a legacy view
  '&, *': {
    boxSizing: 'border-box',
  },

  [`.${classes.title}`]: {
    marginBlockStart: cssVar(cssVariables.modalPadding),
    // Prevent overlap with close button
    marginInlineEnd: `calc(${cssVar(cssVariables.modalPadding)}px + ${Spacing.L}px)`,
  },
  [`.${classes.body}, .${classes.footer}`]: {
    // This *should* just be `rowGap` directly on `modalWrapper`, but that causes layout issues because things like
    // dropdown contents render as children within the wrapper, and thus add extra gaps.
    marginBlockStart: Spacing.XS * 10.5,
  },
  [`.${classes.title} ~ .${classes.body}`]: {
    marginBlockStart: Spacing.M,
  },
  [`.${classes.body}`]: {
    // This enables transitions to and from keywords like 'auto'. Currently (2026-02-17) this doesn't have broad support
    // in browsers, so there's still JavaScript that handles the resizing, but this is the standardized CSS-only
    // approach for the future.
    interpolateSize: 'allow-keywords',
    marginBlockEnd: cssVar(cssVariables.modalPadding),

    [`&:has(+ .${classes.footer})`]: {
      marginBlockEnd: 0,
    },
  },
  [`.${classes.body}, .${classes.title}`]: {
    marginInline: cssVar(cssVariables.modalPadding),
  },
  [`.${classes.close}`]: {
    insetInlineEnd: cssVar(cssVariables.modalPadding),
    insetBlockStart: cssVar(cssVariables.modalPadding),
    position: 'absolute',
  },
})

export const modalTitle = css({
  color: cssVar(ThemedColors.TextPrimary),
  columnGap: Spacing.S,
  display: 'flex',

  [`.${classes.titleText}`]: {
    marginBlock: 0,
    ...Typography.H300,
  },
})

export const modalBody = css({
  color: cssVar(ThemedColors.TextPrimary),

  p: {
    marginBlock: '1em',
    ...Typography.T500,
  },
  'p:first-of-type': {
    marginBlockStart: 0,
  },
  'p:last-of-type': {
    marginBlockEnd: 0,
  },
})

export const multiStep = css({
  marginBlockStart: Spacing.L,
  marginInline: cssVar(cssVariables.modalPadding),
  position: 'relative',

  [`.${classes.progressBar}`]: {
    display: 'flex',
    gap: Spacing.XS,
    listStyle: 'none',
    marginBlockEnd: Spacing.L,
    padding: 0,

    'button[data-state]': {
      backgroundColor: cssVar(ThemedColors.Surface3),
      blockSize: Spacing.XS * 1.5,
      border: 'none',
      borderRadius: CornerRadius.Small,
      color: 'inherit',
      cursor: 'pointer',
      flex: '1 1 0',
      ...Transition(['background-color', 'box-shadow']),

      '&[data-state="active"]': {
        cursor: 'default',
      },
      '&.is-previous, &[data-state="active"]': {
        backgroundColor: cssVar(ThemedColors.Primary6),
      },
      '&.is-previous:hover': {
        backgroundColor: cssVar(ThemedColors.Primary7),
      },
      '&.is-previous:active': {
        backgroundColor: cssVar(ThemedColors.Primary8),
      },
      '&.is-future:hover': {
        backgroundColor: cssVar(ThemedColors.Surface4),
      },
      '&.is-future': {
        backgroundColor: cssVar(ThemedColors.Surface5),
      },
    },
  },

  [`.${classes.multiStepStepTitle}`]: {
    marginBlockEnd: Spacing.S,
    marginBlockStart: 0,
    ...Typography.H100,
  },

  [`.${classes.multiStepCarouselWrapper}`]: {
    overflow: 'hidden',
  },

  [`.${classes.multiStepCarousel}`]: {
    // Makes each step use its own block-size instead of expanding to match the step with the largest block-size.
    alignItems: 'flex-start',
    display: 'flex',
    inlineSize: 'calc(var(--step-count) * 100%)',
    // Prevent scrollbars: visible overflow isn't necessary for the animated resize to work, and having a visible
    // vertical scrollbar messes with the body `inline-size` `calc()` because the size of the scrollbar is subtracted
    // from the container, so on step N part of step N + 1 will clip into its inline-end, and the problem worsens as the
    // step number increases.
    overflow: 'hidden',
    position: 'relative',

    // When animating to a step with a larger `block-size` than the current step the larger step would almost certainly
    // display a scrollbar, which would continuously redraw as the carousel's `block-size` animates before disappearing
    // at the end of the animation.
    [`&[data-animating="true"] .${classes.body}[data-state="active"]`]: {
      overflow: 'hidden',
    },

    [`.${classes.body}`]: {
      flex: '0 0 auto',
      inlineSize: 'calc(100% / var(--step-count))',
      // Remove normal `<Body>` margins; instead the margins are applied to the
      margin: 0,
      opacity: 0.3,
      position: 'relative',
      ...Transition(['opacity']),

      '&[data-state="active"]': {
        opacity: 1,
      },
    },

    '@media (prefers-reduced-motion: reduce)': {
      inlineSize: '100%',

      [`.${classes.body}`]: {
        inlineSize: '100%',
        opacity: 0,
        position: 'absolute',
      },
    },
  },
})
export const multiStepDynamic = (theme: Theme) => ({
  [`.${classes.progressBar} button[data-state]:focus`]: {
    outline: 'none',
    ...FocusIndicator[theme],
  },
})

export const multiStepFooter = css({
  [`.${classes.multiStepPreviousStepButton}`]: {
    marginInlineEnd: 'auto',
  },
})

export const modalFooter = css({
  borderBlockStartColor: `color(from
    ${cssVar(ThemedColors.StrokeSecondary4)}
    display-p3 r g b / 0.08
  )`,
  borderBlockStartStyle: 'solid',
  borderBlockStartWidth: LineWeight.Small,
  columnGap: Spacing.XS * 3,
  display: 'flex',
  justifyContent: 'flex-end',
  padding: Spacing.M,
})

export const description = css({
  color: cssVar(ThemedColors.TextSecondary),
  marginBlockStart: 0,
  ...Typography.T500,

  '&:last-of-type': {
    marginBlockEnd: 0,
  },
})

export const modalClose = css({
  blockSize: Spacing.L,
  inlineSize: Spacing.L,
})
